/**
 * QR codes, the printable A4 poster and the PDF summary for payment links.
 */

import QRCode from 'qrcode';
import type { PaymentLink, PaymentLinkPayment } from '@masonic-tools/shared-types';
import {
  amountRuleLabel,
  formatDateTime,
  formatPence,
  METHOD_PROVIDER_NAMES,
  paymentsByDay,
  totalPayments,
} from '@/lib/unit-payment-links';

const QR_DARK = '#1E3A5F';

export async function paymentLinkQrPng(url: string, width = 1024): Promise<string> {
  return QRCode.toDataURL(url, { width, margin: 2, errorCorrectionLevel: 'M', color: { dark: QR_DARK, light: '#FFFFFF' } });
}

export async function paymentLinkQrSvg(url: string): Promise<string> {
  return QRCode.toString(url, { type: 'svg', margin: 2, errorCorrectionLevel: 'M', color: { dark: QR_DARK, light: '#FFFFFF' } });
}

/** Copy the QR image to the clipboard where the browser supports it. */
export async function copyQrImageToClipboard(pngDataUrl: string): Promise<boolean> {
  try {
    if (typeof ClipboardItem === 'undefined' || !navigator.clipboard?.write) return false;
    const blob = await (await fetch(pngDataUrl)).blob();
    await navigator.clipboard.write([new ClipboardItem({ [blob.type]: blob })]);
    return true;
  } catch {
    return false;
  }
}

function wrap(doc: import('jspdf').jsPDF, text: string, maxWidth: number): string[] {
  return doc.splitTextToSize(text, maxWidth) as string[];
}

/** A4 poster for tables at the festive board: title, description, a large QR, short link and reference. */
export async function buildPaymentLinkPoster(link: PaymentLink, unitName: string): Promise<Blob> {
  const { default: jsPDF } = await import('jspdf');
  const doc = new jsPDF({ unit: 'mm', format: 'a4' });
  const pageW = 210;
  const margin = 20;
  const contentW = pageW - margin * 2;
  let y = 28;

  doc.setTextColor(QR_DARK);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(14);
  doc.text(unitName, pageW / 2, y, { align: 'center' });
  y += 14;

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(28);
  for (const line of wrap(doc, link.title, contentW)) {
    doc.text(line, pageW / 2, y, { align: 'center' });
    y += 12;
  }

  if (link.description) {
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(13);
    doc.setTextColor('#374151');
    for (const line of wrap(doc, link.description, contentW).slice(0, 5)) {
      doc.text(line, pageW / 2, y, { align: 'center' });
      y += 6.5;
    }
  }

  y += 6;
  const qrSize = 110;
  const png = await paymentLinkQrPng(link.shortUrl, 1200);
  doc.addImage(png, 'PNG', (pageW - qrSize) / 2, y, qrSize, qrSize);
  y += qrSize + 10;

  doc.setTextColor(QR_DARK);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(16);
  doc.text('Scan to give', pageW / 2, y, { align: 'center' });
  y += 9;
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(13);
  doc.text(link.shortUrl.replace(/^https?:\/\//, ''), pageW / 2, y, { align: 'center' });
  y += 8;
  doc.setFontSize(11);
  doc.setTextColor('#4b5563');
  doc.text(`Reference: ${link.reference}  ·  ${amountRuleLabel(link)}`, pageW / 2, y, { align: 'center' });

  doc.setFontSize(9);
  doc.text(`Secure payment by ${METHOD_PROVIDER_NAMES[link.paymentMethod]}`, pageW / 2, 285, { align: 'center' });
  return doc.output('blob');
}

/** PDF summary: totals, a by-day breakdown and the list of payments. */
export async function buildPaymentsSummaryPdf(params: {
  unitName: string;
  title: string;
  reference: string;
  payments: PaymentLinkPayment[];
  linkTitles: Record<string, string>;
}): Promise<Blob> {
  const { default: jsPDF } = await import('jspdf');
  const doc = new jsPDF({ unit: 'mm', format: 'a4' });
  const margin = 15;
  let y = 20;
  const totals = totalPayments(params.payments);

  const ensureSpace = (h: number) => {
    if (y + h > 280) {
      doc.addPage();
      y = 20;
    }
  };

  doc.setTextColor(QR_DARK);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(18);
  doc.text(params.title, margin, y);
  y += 7;
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  doc.setTextColor('#4b5563');
  doc.text(`${params.unitName}  ·  Reference ${params.reference}  ·  Generated ${formatDateTime(new Date())}`, margin, y);
  y += 10;

  doc.setTextColor('#111827');
  doc.setFontSize(11);
  const summary: Array<[string, string]> = [
    ['Payments', String(totals.count)],
    ['Collected', formatPence(totals.grossPence)],
    ['Fees', formatPence(totals.feesPence)],
    ['Received after fees', formatPence(totals.netPence)],
    ['Gift Aid declarations', `${totals.giftAidCount} (${formatPence(totals.giftAidEligiblePence)} eligible)`],
  ];
  for (const [k, v] of summary) {
    doc.text(k, margin, y);
    doc.text(v, 120, y);
    y += 6;
  }

  y += 4;
  doc.setFont('helvetica', 'bold');
  doc.text('By day', margin, y);
  y += 6;
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  for (const row of paymentsByDay(params.payments)) {
    ensureSpace(6);
    doc.text(row.day, margin, y);
    doc.text(`${row.count} payment${row.count === 1 ? '' : 's'}`, 60, y);
    doc.text(formatPence(row.grossPence), 110, y);
    doc.text(formatPence(row.netPence), 150, y);
    y += 5.5;
  }

  y += 6;
  ensureSpace(12);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.text('Payments', margin, y);
  y += 6;
  doc.setFontSize(9);
  const cols = [margin, 45, 105, 130, 150, 172];
  ['Date', 'Payer', 'Amount', 'Fee', 'Net', 'Gift Aid'].forEach((h, i) => doc.text(h, cols[i], y));
  y += 5;
  doc.setFont('helvetica', 'normal');
  for (const p of params.payments.filter((x) => x.status === 'paid')) {
    ensureSpace(5);
    const d = p.paidAt || p.createdAt;
    const payer = p.anonymous ? 'Anonymous' : p.payerName || '';
    doc.text(d ? d.toLocaleDateString('en-GB') : '', cols[0], y);
    doc.text(payer.length > 32 ? `${payer.slice(0, 31)}…` : payer, cols[1], y);
    doc.text(formatPence(p.amountPence), cols[2], y);
    doc.text(formatPence(p.processingFeePence), cols[3], y);
    doc.text(formatPence(p.netToUnitPence), cols[4], y);
    doc.text(p.giftAid ? 'Yes' : '', cols[5], y);
    y += 5;
  }
  return doc.output('blob');
}
